import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MASTER_LOGO, DIVISIONS } from "@/data/divisions";

/**
 * Cinematic on-load intro:
 *  1. Master 2TProd Studios logo stamps center
 *  2. Rapid stamp-cycle through the 7 division logos (each ~380ms)
 *  3. Master returns as the "final signature"
 *  4. Dramatic exit — 8 vertical black shutters slide up in staggered timing
 *     while the master logo scale-morphs toward the nav position.
 *  Counter 000 → 100 runs synced across the whole thing.
 *
 *  Session-scoped: only shows once per browser session.
 */

// Ordered slide sequence for the reel: master → 7 divisions. Ends on 2TProd.
const REEL = [
  { logo: MASTER_LOGO, label: "2TProd Studios", accent: "#ec4899" },
  ...DIVISIONS.map((d) => ({
    logo: d.logo,
    label: d.name,
    accent: d.accent,
    logoBlend: d.logoBlend,
    logoClass: d.logoClass,
  })),
];

const SLIDE_MS = 900; // duration a single logo is on screen (well > transition)
const MASTER_HOLD_MS = 1200; // first (master) hold
const FINAL_HOLD_MS = 1600; // last (2TProd) hold before exit
const TRANSITION_MS = 500; // enter / exit motion duration
const EXIT_MS = 1500; // shutter exit duration
const COUNTER_TOTAL_MS =
  MASTER_HOLD_MS + SLIDE_MS * (REEL.length - 2) + FINAL_HOLD_MS;

const Intro = ({ onDone }) => {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !window.sessionStorage.getItem("2t_intro_seen");
  });
  const [idx, setIdx] = useState(0); // current slide in REEL
  const [count, setCount] = useState(0);
  const [exiting, setExiting] = useState(false);

  // Lock scroll
  useEffect(() => {
    if (!show) {
      onDone?.();
      return undefined;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [show, onDone]);

  // Reel timing
  useEffect(() => {
    if (!show) return undefined;
    const timers = [];

    // Cycle divisions after master hold
    const first = REEL.length - 1; // last index (final master)
    let t = MASTER_HOLD_MS;
    for (let i = 1; i < REEL.length; i += 1) {
      const idxCopy = i;
      const dur = i === first ? SLIDE_MS : SLIDE_MS;
      timers.push(setTimeout(() => setIdx(idxCopy), t));
      t += dur;
    }
    // Start exit after final master hold
    timers.push(
      setTimeout(() => {
        setExiting(true);
      }, COUNTER_TOTAL_MS)
    );
    // Fully teardown
    timers.push(
      setTimeout(() => {
        try {
          window.sessionStorage.setItem("2t_intro_seen", "1");
        } catch {
          /* ignore */
        }
        setShow(false);
        onDone?.();
      }, COUNTER_TOTAL_MS + EXIT_MS)
    );

    return () => timers.forEach(clearTimeout);
  }, [show, onDone]);

  // Counter 0 → 100
  useEffect(() => {
    if (!show) return undefined;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / COUNTER_TOTAL_MS);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [show]);

  const current = REEL[idx] || REEL[0];
  const SHUTTER_COLS = 8;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro"
          className="fixed inset-0 z-[200] bg-black overflow-hidden"
          data-testid="intro-overlay"
        >
          {/* Corner marks */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: exiting ? 0 : 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute top-6 left-6 md:top-10 md:left-10 flex items-center gap-3 text-[0.65rem] tracking-[0.32em] uppercase text-white/60"
          >
            <span
              className="w-8 h-px"
              style={{ background: current.accent }}
            />
            {current.label}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: exiting ? 0 : 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute top-6 right-6 md:top-10 md:right-10 text-[0.65rem] tracking-[0.32em] uppercase text-white/60"
          >
            {String(idx + 1).padStart(2, "0")} / {String(REEL.length).padStart(2, "0")}
            <span className="opacity-40 mx-2">·</span>
            Loading experience
          </motion.div>

          {/* Accent halo behind the current logo */}
          <motion.div
            key={`halo-${idx}`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: exiting ? 0 : 0.55, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div
              className="w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full"
              style={{
                background: `radial-gradient(circle, ${current.accent}55 0%, transparent 60%)`,
                filter: "blur(60px)",
              }}
            />
          </motion.div>

          {/* Center logo stack — swap with AnimatePresence */}
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={idx}
                src={current.logo}
                alt=""
                initial={{ opacity: 0, scale: 1.3, rotate: -6, y: 30 }}
                animate={
                  exiting
                    ? { opacity: 0, scale: 1.6, y: -20 }
                    : { opacity: 1, scale: 1, rotate: 0, y: 0 }
                }
                exit={{ opacity: 0, scale: 0.85, rotate: 4, y: -30 }}
                transition={
                  exiting
                    ? { duration: 0.55, ease: [0.76, 0, 0.24, 1] }
                    : { duration: TRANSITION_MS / 1000, ease: [0.22, 1, 0.36, 1] }
                }
                className={
                  current.logoClass
                    ? "max-h-[44vh] w-auto object-contain"
                    : "w-[70vw] md:w-[52vw] lg:w-[44vw] max-w-[860px] max-h-[52vh] object-contain"
                }
                style={
                  current.logoBlend
                    ? { mixBlendMode: current.logoBlend }
                    : undefined
                }
              />
            </AnimatePresence>
          </div>

          {/* Progress bar under logo */}
          <div className="absolute bottom-[24vh] left-1/2 -translate-x-1/2 w-[38vw] max-w-[520px] h-px bg-white/15 overflow-hidden">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: count / 100 }}
              transition={{ duration: 0.2, ease: "linear" }}
              className="h-full origin-left"
              style={{ background: current.accent }}
            />
          </div>

          {/* Bottom counter */}
          <motion.div
            animate={{ opacity: exiting ? 0 : 1, y: exiting ? 20 : 0 }}
            transition={{ duration: 0.4 }}
            className="absolute bottom-8 md:bottom-12 left-8 md:left-14 font-display text-white text-[16vw] md:text-[10vw] leading-none pointer-events-none"
          >
            {count.toString().padStart(3, "0")}
          </motion.div>
          <motion.div
            animate={{ opacity: exiting ? 0 : 1, y: exiting ? 20 : 0 }}
            transition={{ duration: 0.4 }}
            className="absolute bottom-8 md:bottom-12 right-8 md:right-14 flex flex-col items-end gap-2 pointer-events-none"
          >
            <div className="text-white/50 text-[0.65rem] tracking-[0.32em] uppercase">
              Ink · Light · Motion
            </div>
            <div
              className="font-editorial italic text-4xl md:text-6xl leading-none"
              style={{ color: current.accent }}
            >
              {idx === 0
                ? "welcome."
                : idx === REEL.length - 1
                ? "seven / seven."
                : "chapter " + String(idx).padStart(2, "0")}
            </div>
          </motion.div>

          {/* Exit shutters — 8 vertical black strips slide up in stagger */}
          {exiting && (
            <div className="absolute inset-0 flex pointer-events-none z-[210]">
              {Array.from({ length: SHUTTER_COLS }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 0 }}
                  animate={{ y: "-100%" }}
                  transition={{
                    duration: 1.1,
                    delay: i * 0.05,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                  className="flex-1 bg-black border-r border-white/[0.02] last:border-r-0"
                />
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Intro;
